import {DependencyList, SetStateAction, useEffect, useMemo, useReducer} from 'react';

const {is} = Object;

/**
 * Writable computation is a pattern that combines behaviors of
 * computed states and stored states:
 * - the value equals computed result until it is written
 * - then the value equals what was written until it is recomputed
 *
 * ### ⚠️ Warning. Concurrent mode ⚠️
 * #### Behavior might be unexpected.
 * Recomputation takes priority over any writes. In a normal useState hook
 * any updates inside startTransition (non-urgent updates) replace
 * those that came before:
 * ```
 * startTransition(() => setState("A"))
 * startTransition(() => setState("B"))
 * startTransition(() => setState("C"))
 * // updates A and B will be dropped
 * ```
 *
 * In a useWritable writes cannot replace recomputation
 * ```
 * const [,setWritable] = useWritable(() => dep.toUpperCase(), [dep])
 * startTransition(() => setDep("a"))
 * startTransition(() => setWritable("B"))
 * startTransition(() => setWritable("C"))
 * // updated B and C will be dropped
 * ```
 * Once update triggered by `setDep` resolves, value of the writable will be `"A"`,
 * because that's when react thinks `dep` is actually updated. Even if updated triggered
 * by `setWritable` resolves after `setDep`, the value will remain `"A"`.
 *
 * Making this hook work same way for computes and for writes is extremely difficult
 * and might be impossible.
 */
export function useWritable<T>(computation: () => T, deps: DependencyList) {
    let runRenderEffect = function normalRenderEffect() {
        ref.current = {compute: computation()};
    };
    const [ref, write] = useReducer(reduceWrites<T>, null, function initializer() {
        runRenderEffect = doNothing;
        return {
            committed: computation(),
            current: {},
        };
    });

    useMemo(runRenderEffect, deps);

    const value = ref.current.write || ref.current.compute || ref.committed;
    useEffect(() => {
        ref.committed = value;
        ref.current = {};
    }, [value]);

    return [value, write] as const;
}

function reduceWrites<T>(state: {committed: T; current: {compute?: T; write?: T}}, update: SetStateAction<T>) {
    const value = state.committed;
    if (update instanceof Function) {
        update = update(value);
    }
    if (!is(value, update)) {
        return {...state, current: {write: update}};
    }
    return state;
}

function doNothing() {
    // noop
}
